import { useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardText, Kanban, TextalignJustifycenter, Grid8 } from "iconsax-react";
import { Button, Prompt, PromptForm } from "@/shared/ui";
import { useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import type { TaskStatus } from "../../types";
import { useBoardPage } from "../../hooks/useBoardPage";
import { useBoardFilters } from "../../hooks/useBoardFilters";
import { useBoardMutations } from "../../hooks/useBoardMutations";
import { useBoardModals } from "../../hooks/useBoardModals";
import { TASK_STATUS_MAP } from "../../consts";
import {
  BoardPageSkeleton,
  OverviewTabContent,
  ExportDropdown,
  BoardTabs,
  GeneralKanbanView,
  TaskFormModal,
  TaskDetailModal,
  BoardFilterBar,
  TasksListView,
  BacklogTableModal,
} from "./components";
import type { BoardTab } from "./components";

export default function BoardPage() {
  const state = useBoardPage();
  const { hasPermission, user } = useAuth();
  const { t: tTasks } = useTranslation("TasksPage");
  const canManage = hasPermission(PERMISSIONS.PROJECTS.MANAGE_TASKS);
  const canReorder = hasPermission(PERMISSIONS.PROJECTS.REORDER_TASKS);
  const canComment = hasPermission(PERMISSIONS.PROJECTS.ADD_COMMENTS);
  const canUpdateOwnStatus = hasPermission(PERMISSIONS.PROJECTS.UPDATE_OWN_STATUS);
  const canDragAll = canManage;
  const currentUserId = user?.id ?? null;

  const filters = useBoardFilters({
    projectBoards: state.crossProjectBoard.projectBoards,
    allAssignees: state.crossProjectBoard.allAssignees,
    isSingleProject: state.isSingleProject,
    projectId: state.projectId,
    kpiId: state.kpiId,
    activeTab: state.activeTab,
    onClearKpi: () => state.handleKpiFilter(null),
  });

  const mutations = useBoardMutations({ t: tTasks });

  const modals = useBoardModals({
    isSingleProject: state.isSingleProject,
    projectId: state.projectId,
  });

  const taskIdProcessedRef = useRef(false);
  useEffect(function openTaskFromUrl() {
    if (state.initialTaskId && state.projectId && !taskIdProcessedRef.current) {
      taskIdProcessedRef.current = true;
      modals.openTaskById(state.initialTaskId, state.projectId);
      state.clearTaskIdParam();
    }
  }, [state.initialTaskId, state.projectId, modals.openTaskById, state.clearTaskIdParam]);

  const statusLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [key, val] of Object.entries(TASK_STATUS_MAP)) {
      map[key] = tTasks(val.label);
    }
    return map;
  }, [tTasks]);

  const pageTitle = filters.activeProject
    ? `${filters.activeProject.name} — ${state.t("meta.title")}`
    : state.t("meta.title");

  const headerTitle = filters.activeProject
    ? state.t("header.projectTitle", { name: filters.activeProject.name })
    : state.t("header.title");

  useBreadcrumbOverrides(
    filters.activeProject ? { boardProjectName: filters.activeProject.name } : {},
  );

  const tabItems: BoardTab[] = [
    { id: "overview", label: state.t("tabs.overview"), icon: ClipboardText },
    { id: "kanban", label: state.t("tabs.kanban"), icon: Kanban },
    { id: "list", label: state.t("tabs.list"), icon: TextalignJustifycenter },
  ];

  const showFilterBar = state.activeTab === "list" || state.activeTab === "kanban";

  return (
    <>
      <title>{pageTitle}</title>
      <section className="flex-1 flex flex-col pr-1">
        <div className="shrink-0">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-component-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{headerTitle}</h1>
            <div className="flex items-center gap-2">
              <ExportDropdown onExportTasks={state.handleExportPdf} onExportAudit={state.handleExportAuditPdf} />
              <Button
                variant="secondary"
                size="lg"
                className="radius-xs! flex items-center gap-2"
                onClick={() => state.handleProjectFilter(null)}
              >
                <Grid8 size={20} color="currentColor" />
                {state.t("header.generalBoard")}
              </Button>
            </div>
          </div>

          <BoardTabs items={tabItems} activeId={state.activeTab} onChange={state.handleTabChange} />
        </div>

        {state.activeTab === "overview" ? (
          <div className="flex-1 min-h-0 mt-4">
            {state.isCrossProjectLoading ? (
              <BoardPageSkeleton activeTab={state.activeTab} isSingleProject={state.isSingleProject} />
            ) : (
              <OverviewTabContent
                projectId={state.isSingleProject ? state.projectId : null}
                onSelectProject={state.handleProjectFilter}
                projectBoards={filters.activeProjectBoards}
                activeAssignees={filters.activeAssignees}
                onTaskClick={modals.handleTaskClick}
              />
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 mt-4 p-7 radius-3xl surface-base-fill">
            {showFilterBar && (
              <BoardFilterBar
                kpiFilter={state.kpiId}
                onKpiChange={state.handleKpiFilter}
                kpiTasks={filters.kpiTasks}
                priorityFilter={filters.priorityFilter}
                onPriorityChange={filters.setPriorityFilter}
                assigneeFilter={filters.assigneeFilter}
                onAssigneeChange={filters.setAssigneeFilter}
                assignees={filters.activeAssignees}
                favoritesOnly={filters.favoritesOnly}
                onFavoritesToggle={filters.setFavoritesOnly}
                kpiFlagOnly={filters.kpiFlagOnly}
                onKpiFlagToggle={filters.setKpiFlagOnly}
                onCreateTask={canManage ? () => modals.handleCreateTask() : undefined}
                canCreate={canManage}
                dueDateRange={filters.dueDateRange}
                onDueDateChange={filters.setDueDateRange}
                isBacklogActive={filters.isBacklogActive}
                onBacklogToggle={filters.setIsBacklogActive}
                hasActiveFilter={filters.hasActiveFilter}
                onResetFilters={filters.handleResetFilters}
              />
            )}
            {state.isCrossProjectLoading ? (
              <BoardPageSkeleton activeTab={state.activeTab} />
            ) : (
              <>
                {state.activeTab === "list" && (
                  <TasksListView
                    projectBoards={filters.filteredProjectBoards}
                    onTaskClick={modals.handleTaskClick}
                    onCreateTask={canManage ? modals.handleCreateTask : undefined}
                    onNavigateToProject={!state.isSingleProject ? state.handleProjectFilter : undefined}
                  />
                )}
                {state.activeTab === "kanban" && (
                  <GeneralKanbanView
                    projectBoards={filters.filteredProjectBoards}
                    onTaskClick={modals.handleTaskClick}
                    onCreateTask={canManage ? modals.handleCreateTask : undefined}
                    onReorderError={mutations.handleReorderError}
                    canReorder={canReorder}
                    canDragAll={canDragAll}
                    currentUserId={currentUserId}
                    isBacklogActive={filters.isBacklogActive}
                    onNavigateToProject={!state.isSingleProject ? state.handleProjectFilter : undefined}
                  />
                )}
              </>
            )}
          </div>
        )}
      </section>

      {modals.detailTask && (
        <TaskDetailModal
          task={modals.selectedTaskData}
          taskId={modals.detailTask.task.id}
          projectId={modals.detailTask.projectId}
          isLoading={modals.isTaskLoading}
          isTaskFetching={modals.isTaskFetching}
          statusOverride={mutations.statusOverrides[modals.detailTask.task.id] ?? modals.detailTask.task.status}
          projectName={
            filters.filteredProjectBoards.find((b) => b.project.id === modals.detailTask!.projectId)?.project.name ?? ""
          }
          onClose={modals.closeDetailModal}
          onEdit={modals.handleEditTask}
          onDelete={() => {
            if (modals.selectedTaskData) {
              mutations.setConfirmDelete(modals.selectedTaskData.id);
            }
          }}
          canManage={canManage}
          canComment={canComment}
          canUpdateOwnStatus={canUpdateOwnStatus}
          currentUserId={currentUserId}
          onStatusChange={(taskId: number, newStatus: TaskStatus) =>
            mutations.handleStatusChange(taskId, newStatus, modals.detailTask!.projectId)
          }
          statusLabelMap={statusLabelMap}
          isFavorite={modals.selectedTaskData?.is_favorite ?? false}
          onToggleFavorite={() =>
            mutations.handleToggleFavorite(modals.detailTask!.task.id, modals.detailTask!.projectId)
          }
          onViewBoard={
            modals.selectedTaskData?.task_type === "kpi"
              ? () => {
                  const taskId = modals.detailTask!.task.id;
                  modals.closeDetailModal();
                  state.handleNavigateToKpiBoard(taskId);
                }
              : undefined
          }
        />
      )}

      {modals.taskFormState && (
        <TaskFormModal
          onClose={modals.closeTaskForm}
          onSuccess={modals.closeTaskForm}
          isRefetching={state.isCrossProjectFetching}
          defaultStatus={modals.taskFormState.defaultStatus}
          mode={modals.taskFormState.mode}
          task={modals.taskFormState.task}
          assignees={filters.activeAssignees}
          projectId={modals.taskFormState.projectId}
          kpiTasks={filters.kpiTasks}
          forceKpi={modals.taskFormState.forceKpi}
          projects={
            modals.taskFormState.projectId === 0
              ? state.crossProjectBoard.projectBoards.map((b) => b.project)
              : undefined
          }
        />
      )}

      {mutations.confirmDelete !== null && modals.detailTask && (
        <PromptForm
          onClose={() => mutations.setConfirmDelete(null)}
          onConfirm={() =>
            mutations.handleDeleteConfirm(mutations.confirmDelete!, modals.detailTask!.projectId, modals.closeDetailModal)
          }
          title={tTasks("confirm.deleteTaskTitle")}
          text={tTasks("confirm.deleteTaskText")}
          variant="error"
          isLoading={mutations.isDeletingTask}
          confirmText={tTasks("confirm.delete")}
          cancelText={tTasks("confirm.cancel")}
          namespace="TasksPage"
        />
      )}

      {mutations.prompt && (
        <Prompt
          onClose={() => mutations.setPrompt(null)}
          title={mutations.prompt.title}
          text={mutations.prompt.text}
          variant={mutations.prompt.variant}
          namespace="TasksPage"
        />
      )}

      {mutations.reorderError && (
        <Prompt
          onClose={() => mutations.setReorderError(false)}
          title={tTasks("messages.reorderErrorTitle")}
          text={tTasks("messages.reorderErrorText")}
          variant="error"
          namespace="TasksPage"
        />
      )}

      {state.auditPrompt && (
        <Prompt
          onClose={() => state.setAuditPrompt(null)}
          title={state.auditPrompt.title}
          text={state.auditPrompt.text}
          variant={state.auditPrompt.variant}
          namespace="TasksPage"
        />
      )}

      {filters.isBacklogActive && state.activeTab === "list" && (
        <BacklogTableModal
          tasks={filters.backlogTasks}
          projectName={filters.activeProject?.name ?? ""}
          onClose={() => filters.setIsBacklogActive(false)}
          onStatusChange={
            state.isSingleProject && state.projectId
              ? (taskId: number, newStatus: TaskStatus) =>
                  mutations.handleStatusChange(taskId, newStatus, state.projectId!)
              : () => {}
          }
        />
      )}
    </>
  );
}
